import { unsafeCSS, html, TemplateResult } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { customElement, property } from 'lit/decorators.js';
import { ApiService } from '@trp-dms-digital-shared/ds-ui-library-api';

import { PopApiBaseComponent } from '../../base/pop-api-base-component.js';
import { TeamMember } from '../../shared/team-member/team-member.model.js';
import { KeyFact } from '../../shared/key-fact/key-fact.model.js';

import type { Overview } from './overview.model.js';
import style from './overview.scss?inline';
import { Constants } from './overview.constants.js';
import { Constants as BaseConstants } from '../../base/base.constants.js';
import { sanitizeHTML } from '../../common/utils/sanitizeHTML.js';
import { OverviewFragments } from '../../common/fragments/overview.fragments.js';

/**
 * @tag pop-overview-component
 * @property {Overview} [componentObject] Overview content
 * @attribute {Overview} [data-component-object] Overview content
 */
@customElement(Constants.TAG_NAME)
export class PopOverviewComponent extends PopApiBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  /**
   * @internal
   */
  static readonly nestedComponents = Constants.NESTED_COMPONENTS;

  /**
   * @internal
   */
  fragment = OverviewFragments.overviewFragment;

  /**
   * The component object that contains the pre-fetched data to be rendered.
   */
  @property({
    attribute: BaseConstants.DATA_COMPONENT_OBJECT,
    type: Object,
  })
  componentObject?: Overview;

  constructor() {
    super(new ApiService());
  }

  protected processResponse(_response): void {
    this.componentObject = _response.overviewExhibit;
  }

  private renderObjective(objective?: string): TemplateResult {
    if (objective) {
      return html`
        <h3 class="overview-objective">
          ${unsafeHTML(sanitizeHTML(objective))}
        </h3>
      `;
    }

    return html``;
  }

  private renderDescription(description?: string): TemplateResult {
    if (description) {
      return html`
        <div class="overview-description">
          ${unsafeHTML(sanitizeHTML(description))}
        </div>
      `;
    }

    return html``;
  }

  private renderStrategy(strategy?: string): TemplateResult {
    if (strategy) {
      return html`<div class="overview-strategy">
        <h3
          class="b-typography-serif--headline--100 b-font-weight-bold b-margin-block-end--micro--jumbo"
        >
          ${Constants.STRATEGY}
        </h3>
        <div class="authored-strategy">
          ${unsafeHTML(sanitizeHTML(strategy))}
        </div>
      </div>`;
    }

    return html``;
  }

  private renderTeamMembers(teamMembers?: TeamMember[]): TemplateResult {
    if (teamMembers) {
      return html`
        <div class="overview-team-member b-grid">
          ${teamMembers.map(
            (teamMember) => html`
              <div class="b-grid-item--12-col b-md:6-col">
                <pop-team-member-component
                  data-country="${this.country}"
                  data-language="${this.language}"
                  .componentObject=${teamMember}
                ></pop-team-member-component>
              </div>
            `,
          )}
        </div>
      `;
    }

    return html``;
  }

  private renderKeyFactsSection(
    keyFacts?: KeyFact[],
    asOfDate?: string,
  ): TemplateResult {
    return html`
      <div class="overview-key-facts">
        <pop-overview-key-facts-component
          beacon-mode-transform="alternate"
          .componentObject=${{ keyFacts, asOfDate }}
        >
        </pop-overview-key-facts-component>
      </div>
    `;
  }

  render() {
    if (this.componentObject) {
      const {
        productObjective,
        productDescription,
        productStrategy,
        teamMembers,
        keyFacts,
        asOfDate,
      } = this.componentObject;

      return this.renderComponent(html`
        <div class="overview-top-section b-container">
          ${!productObjective && !productDescription && !productStrategy
            ? html` <pop-no-data-available-component
                .message="${Constants.OVERVIEW_UNAVAILABLE}"
              ></pop-no-data-available-component>`
            : html`<div>
                  ${this.renderObjective(productObjective)}
                  ${this.renderDescription(productDescription)}
                </div>
                ${this.renderStrategy(productStrategy)}`}
          ${this.renderTeamMembers(teamMembers)}
        </div>
        ${this.renderKeyFactsSection(keyFacts, asOfDate)}
      `);
    }

    return html``;
  }
}
